import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import marked from 'marked'
import './ai-chat-widget.sass'
import { ConversationRoles } from '@src/lib/mistral-api'
import AIChatLogic from './ai-chat-logic'
import { Modal } from '@src/elements/modal/modal'
import { Textarea } from '@src/elements/input/textarea'
import { AIConversationReportService } from '@src/service/ai-conversation-report'

@Component({
  components: {
    AIChatLogic
  },
  computed: {
    selectedWorkoutExercises () {
      return this.$store.state.new_workout.workoutExercises
    },
    exerciseTags () {
      return this.$store.getters.exerciseTags
    },
    club () {
      return this.$store.state.club
    },
    workout () {
      return this.$store.state.new_workout
    }
  }
})
export default class AIChatWidget extends Vue {
  @Prop({ required: false, default: 'ai-chat-widget' })
  filterFunction: Function

  @Prop({ required: false })
  removeFilterFunction: Function

  @Prop({ required: false, default: 'default' })
  availableTags: any

  // UI State
  private isOpen: boolean = false
  private currentInput: string = ''
  private reportText: string = ''
  private showReportModal: boolean = false
  private isSendingReport: boolean = false
  private reportError: string = ''
  private reportSuccess: string = ''
  private submittedReportFingerprints: Set<string> = new Set()
  private lastMessageCount: number = 0
  private animatingMessageIds: Set<number> = new Set()
  private reportService: AIConversationReportService = new AIConversationReportService()

  // Loading & Event Handling
  private isAITyping: boolean = false
  private currentLoadingMessage: string = 'Thinking'
  private showRetryButton: boolean = false
  private retryTimeout: any = null
  private markdownRenderer: any = null

  mounted () {
    this.loadSubmittedReportFingerprints()
  }

  beforeDestroy () {
    this.cleanupComponent()
  }

  render (h: CreateElement) {
    const AILogicComp: any = AIChatLogic
    const reportConversationLabel = this.$t('AI_ASSISTANT_REPORT_CONVERSATION') as string
    const restartLabel = this.$t('AI_ASSISTANT_RESTART') as string
    const chatButton = (
      <div class='ai-chathead'>
        <button class='ai-chat-button' onClick={this.toggleChat} aria-label='Open AI chat'>
          <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
            <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'></path>
          </svg>
        </button>
      </div>
    )

    const chatBox = (
      <div class='ai-chat-box'>
        <header class='chat-header'>
          <h2>PixFit AI</h2>
          <div class='chat-header-actions'>
            <button onClick={this.openReportModal} aria-label={reportConversationLabel} title={reportConversationLabel} class='chat-report-button'>
              <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'>
                <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z'></path>
                <line x1='12' y1='9' x2='12' y2='13'></line>
                <line x1='12' y1='17' x2='12.01' y2='17'></line>
              </svg>
              <span>{reportConversationLabel}</span>
            </button>
            <button onClick={this.restartConversation} aria-label={restartLabel}>{restartLabel}</button>
            <button onClick={this.toggleChat} aria-label='Close Chat'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
                <line x1='6' y1='19' x2='18' y2='19'></line>
              </svg>
            </button>
          </div>
        </header>

        <main class='chat-messages' ref='messageContainer'>
          {this.renderMessages(h)}
          {this.renderLoadingIndicators(h)}
        </main>

        {this.renderQuestionOptions(h)}
        {this.renderInputArea(h)}
      </div>
    )

    return (
      <div class={`ai-chat-container ${this.isOpen ? 'open' : ''}`}>
        <AILogicComp
          ref='aiLogic'
          filterFunction={this.filterFunction}
          removeFilterFunction={this.removeFilterFunction}
          availableTags={this.availableTags}
          closeChat={this.toggleChat}
          onQuestionAsked={this.forceUpdate}
          onMessageAdded={this.scrollToBottom}
          onConversationUpdated={this.forceUpdate}
          loadingCallbacks={{
            onLoadingStarted: () => this.startLoadingIndicator(),
            onLoadingStopped: (index) => this.stopLoadingIndicator(index),
            onToolLoadingStarted: () => this.startToolLoadingIndicator(),
            onToolLoadingStopped: () => this.stopToolLoadingIndicator(),
            setLoadingMessage: (message) => this.setLoadingMessage(message)
          }}
          onMessageDelta={this.handleMessageDelta}
        />
        {this.isOpen ? chatBox : chatButton}
        {this.renderReportModal(h)}
      </div>
    )
  }

  public setLoadingMessage (message: string): void {
    console.log('Setting loading message:', message, 'isAITyping:', this.isAITyping)
    this.currentLoadingMessage = message
    if (!this.isAITyping) {
      this.startTypingAnimation(message)
    } else {
      console.log('Updating loading message')
      this.$forceUpdate()
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    }
  }

  // AI Logic interface methods
  getAILogic (): any {
    return this.$refs.aiLogic
  }

  private cleanupComponent (): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
      this.retryTimeout = null
    }
  }

  private renderMessages (h: CreateElement) {
    const aiLogic = this.getAILogic()
    if (!aiLogic) return []

    const messages = aiLogic.getMessages()
      .filter((msg) => (msg.role === ConversationRoles.ASSISTANT || msg.role === ConversationRoles.USER))

    const isNewMessage = messages.length > this.lastMessageCount
    if (isNewMessage) {
      // Add new message indexes to animation set
      for (let i = this.lastMessageCount; i < messages.length; i++) {
        this.animatingMessageIds.add(i)
        // Remove animation class after animation completes
        setTimeout(() => {
          this.animatingMessageIds.delete(i)
          this.$forceUpdate()
        }, 500) // Animation duration + buffer
      }
      this.lastMessageCount = messages.length
    }

    return messages.map((msg, index) => {
      const shouldAnimate = this.animatingMessageIds.has(index)

      // Map roles to CSS class names
      const roleClass = msg.role === ConversationRoles.ASSISTANT ? 'ai' : 'user'

      // Skip messages with no content (like tool-only messages)
      const content = msg.content || ''
      if (!content.trim()) {
        return null
      }

      return (
        <div key={index} class={`message message-${roleClass} ${shouldAnimate ? 'animate-in' : ''}`}>
          <p class={'message-bubble'} domPropsInnerHTML={this.parseMarkdown(content)}></p>
        </div>
      )
    }).filter(Boolean)
  }

  private renderLoadingIndicators (h: CreateElement) {
    if (!this.isAITyping) return []

    return [
      <div key='typing' class='message message-ai loading typing-indicator'>
        {this.showRetryButton ? (
          <div class='retry-container'>
            <p>Response is taking longer than expected...</p>
            <button onClick={() => this.retryLastRequest()} class='retry-button'>
              Retry Request
            </button>
          </div>
        ) : (
          <p class='message-bubble'>
            <span class='typing-dots'></span>
            <span class='loading-text'>{'   '}{this.currentLoadingMessage}</span>
          </p>
        )}
      </div>
    ]
  }

  private renderQuestionOptions (h: CreateElement) {
    const aiLogic = this.getAILogic()
    if (!aiLogic) return null

    const currentQuestion = aiLogic.getCurrentQuestion()
    if (!currentQuestion) return null
    const hasReachedUserMessageLimit = aiLogic.hasReachedUserMessageLimit && aiLogic.hasReachedUserMessageLimit()

    return (
      <div class='question-options'>
        <div class='question-text' domPropsInnerHTML={this.parseMarkdown(currentQuestion.question)}></div>
        <div class='option-buttons'>
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              class='option-button'
              onClick={() => this.selectOption(option)}
              disabled={hasReachedUserMessageLimit}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    )
  }

  private renderInputArea (h: CreateElement) {
    const aiLogic = this.getAILogic()
    const conversationStopped = aiLogic ? aiLogic.isConversationStopped() : false
    const hasReachedUserMessageLimit = aiLogic && aiLogic.hasReachedUserMessageLimit && aiLogic.hasReachedUserMessageLimit()
    const isAnsweringQuestion = aiLogic && !!aiLogic.getCurrentQuestion && !!aiLogic.getCurrentQuestion()
    const isDisabled = conversationStopped || hasReachedUserMessageLimit || (this.isAITyping && !isAnsweringQuestion)

    return (
      <footer class='chat-input-area'>
        <input
          ref='chatInput'
          type='text'
          placeholder={this.getInputPlaceholder()}
          value={this.currentInput}
          onInput={this.handleInput}
          onKeydown={(e: KeyboardEvent) => {
            if (e.key === 'Enter' && !isDisabled) this.sendMessage()
          }}
          disabled={isDisabled}
        />
        <button onClick={this.sendMessage} disabled={isDisabled}>Send</button>
      </footer>
    )
  }

  private getInputPlaceholder (): string {
    const aiLogic = this.getAILogic()
    if (!aiLogic) return 'Loading...'

    if (aiLogic.hasReachedUserMessageLimit && aiLogic.hasReachedUserMessageLimit()) {
      return `Message limit reached (${aiLogic.getMaxUserMessages()} user messages) - please restart`
    }
    if (aiLogic.isConversationStopped()) return 'Token limit reached - please restart'
    if (aiLogic.getCurrentQuestion()) return 'Or type your own response...'
    if (this.isAITyping) return `${this.currentLoadingMessage}...`
    return 'Type your message...'
  }

  private renderReportModal (h: CreateElement) {
    if (!this.showReportModal) return null
    const currentFingerprint = this.getCurrentReportFingerprint()
    const alreadySubmitted = !!currentFingerprint && this.submittedReportFingerprints.has(currentFingerprint)
    const isDisabled = this.isSendingReport || alreadySubmitted || !this.reportText.trim()
    const reportTitle = this.$t('AI_ASSISTANT_REPORT_TITLE') as string
    const reportDescription = this.$t('AI_ASSISTANT_REPORT_DESCRIPTION') as string
    const reportPlaceholder = this.$t('AI_ASSISTANT_REPORT_PLACEHOLDER') as string
    const alreadySubmittedLabel = this.$t('AI_ASSISTANT_REPORT_ALREADY_SUBMITTED') as string
    const cancelLabel = this.$t('AI_ASSISTANT_REPORT_CANCEL') as string
    const sendLabel = this.$t('AI_ASSISTANT_REPORT_SEND') as string
    const sendingLabel = this.$t('AI_ASSISTANT_REPORT_SENDING') as string

    return (
      <Modal show={true} title={reportTitle} closeCallback={this.closeReportModal} cssClass='ai-report-modal' overflow='hidden'>
        <div class='ai-report-modal-body'>
          <p>{reportDescription}</p>
          <Textarea
            value={this.reportText}
            placeholder={reportPlaceholder}
            keyup={this.updateReportText}
            change={this.updateReportText}
            maxLength={3000}
          />
          {alreadySubmitted ? <p class='ai-report-status'>{alreadySubmittedLabel}</p> : null}
          {this.reportError ? <p class='ai-report-error'>{this.reportError}</p> : null}
          {this.reportSuccess ? <p class='ai-report-success'>{this.reportSuccess}</p> : null}
        </div>
        <div slot='footer' class='ai-report-actions'>
          <button class='ai-report-secondary' onClick={this.closeReportModal} disabled={this.isSendingReport}>{cancelLabel}</button>
          <button class='ai-report-submit' onClick={this.submitReport} disabled={isDisabled}>
            {this.isSendingReport ? sendingLabel : sendLabel}
          </button>
        </div>
      </Modal>
    )
  }

  private toggleChat (): void {
    this.isOpen = !this.isOpen
    if (this.isOpen) {
      // Clear loading states when opening to ensure clean slate
      this.clearLoadingStates()
      this.$nextTick(() => {
        this.scrollToBottom()
        const input = this.$refs.chatInput as HTMLInputElement
        if (input) input.focus()
      })
    } else {
      this.clearLoadingStates()
    }
  }

  private handleInput (event: Event): void {
    this.currentInput = (event.target as HTMLInputElement).value
  }

  private selectOption (option: string): void {
    const aiLogic = this.getAILogic()
    if (aiLogic && !(aiLogic.hasReachedUserMessageLimit && aiLogic.hasReachedUserMessageLimit())) {
      aiLogic.selectOption(option)
      this.scrollToBottom()
    }
  }

  private sendMessage (): void {
    if (!this.currentInput) return
    const trimmedInput = this.currentInput.trim()
    if (!trimmedInput) return

    const aiLogic = this.getAILogic()
    if (
      aiLogic &&
      !aiLogic.isConversationStopped() &&
      !(aiLogic.hasReachedUserMessageLimit && aiLogic.hasReachedUserMessageLimit())
    ) {
      aiLogic.sendMessage(trimmedInput)
      this.currentInput = ''
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    }
  }

  private restartConversation (): void {
    const aiLogic = this.getAILogic()
    if (aiLogic) {
      aiLogic.restartConversation()
    }
    this.currentInput = ''
    this.lastMessageCount = 0
    this.animatingMessageIds.clear()
    this.clearLoadingStates()
    this.closeReportModal()
    this.scrollToBottom()
  }

  private openReportModal (event?: Event): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.reportError = ''
    this.reportSuccess = ''
    this.showReportModal = true
  }

  private closeReportModal (): void {
    if (this.isSendingReport) return
    this.showReportModal = false
    this.reportText = ''
    this.reportError = ''
    this.reportSuccess = ''
  }

  private updateReportText (value: string): void {
    this.reportText = value
    this.reportError = ''
    this.reportSuccess = ''
  }

  private async submitReport (): Promise<void> {
    const aiLogic = this.getAILogic()
    if (!aiLogic || !this.reportText.trim() || this.isSendingReport) return

    const payload = aiLogic.getReportPayload(this.reportText)
    if (this.submittedReportFingerprints.has(payload.conversationFingerprint)) {
      this.reportError = ''
      this.reportSuccess = this.$t('AI_ASSISTANT_REPORT_ALREADY_SUBMITTED') as string
      this.$forceUpdate()
      return
    }

    this.isSendingReport = true
    this.reportError = ''
    this.reportSuccess = ''
    try {
      await this.reportService.createReport(payload)
      this.submittedReportFingerprints.add(payload.conversationFingerprint)
      this.saveSubmittedReportFingerprints()
      this.reportSuccess = this.$t('AI_ASSISTANT_REPORT_SENT') as string
      this.reportText = ''
    } catch (error) {
      if (error && error.status === 409) {
        this.submittedReportFingerprints.add(payload.conversationFingerprint)
        this.saveSubmittedReportFingerprints()
        this.reportSuccess = this.$t('AI_ASSISTANT_REPORT_ALREADY_SUBMITTED') as string
        this.reportText = ''
      } else {
        this.reportError = this.$t('AI_ASSISTANT_REPORT_ERROR') as string
      }
    } finally {
      this.isSendingReport = false
      this.$forceUpdate()
    }
  }

  private getCurrentReportFingerprint (): string {
    const aiLogic = this.getAILogic()
    if (!aiLogic) return ''
    return aiLogic.getReportPayload('').conversationFingerprint
  }

  private loadSubmittedReportFingerprints (): void {
    try {
      const stored = localStorage.getItem('pixfit-ai-reported-conversations')
      const parsed = stored ? JSON.parse(stored) : []
      this.submittedReportFingerprints = new Set(Array.isArray(parsed) ? parsed : [])
    } catch (e) {
      this.submittedReportFingerprints = new Set()
    }
  }

  private saveSubmittedReportFingerprints (): void {
    try {
      localStorage.setItem('pixfit-ai-reported-conversations', JSON.stringify(Array.from(this.submittedReportFingerprints)))
    } catch (e) {
      // Ignore storage errors; the in-memory guard still prevents repeat submits in this session.
    }
  }

  // Event handlers for AI Logic component
  private handleMessageDelta ({ index, delta }): void {
    // Don't interfere with existing loading states during streaming
    this.scrollToBottom()
    this.$forceUpdate()
  }

  private forceUpdate (): void {
    this.$forceUpdate()
  }

  private startTypingAnimation (message: string = 'Thinking'): void {
    this.isAITyping = true
    this.currentLoadingMessage = message
    this.$forceUpdate()
    this.scrollToBottom()
  }

  private stopTypingAnimation (): void {
    this.isAITyping = false
    this.$forceUpdate()
  }

  private startLoadingIndicator (): number {
    this.startTypingAnimation('Thinking')

    // Show retry button after 10 seconds
    this.retryTimeout = setTimeout(() => {
      this.showRetryButton = true
      this.$forceUpdate()
    }, 10000)

    return 0
  }

  private stopLoadingIndicator (loadingIndex: number): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
      this.retryTimeout = null
    }
    this.showRetryButton = false
    this.stopTypingAnimation()
    this.$forceUpdate()
  }

  private startToolLoadingIndicator (): void {
    // Tools will use specific loading messages
    this.$forceUpdate()
    this.scrollToBottom()
  }

  private stopToolLoadingIndicator (): void {
    this.stopTypingAnimation()
  }

  private clearLoadingStates (): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
      this.retryTimeout = null
    }
    this.showRetryButton = false
    this.stopTypingAnimation()
    this.$forceUpdate()
  }

  private retryLastRequest (): void {
    this.clearLoadingStates()
    const aiLogic = this.getAILogic()
    if (aiLogic && !aiLogic.isConversationStopped()) {
      aiLogic.retryLastRequest()
    }
  }

  // UI utility methods

  private scrollToBottom (): void {
    this.$nextTick(() => {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer as HTMLElement
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    })
  }

  private parseMarkdown (content: string): string {
    if (!content) return ''
    return marked.parse(this.escapeHtml(content), { renderer: this.getMarkdownRenderer() })
  }

  private escapeHtml (content: string): string {
    return content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  private getMarkdownRenderer (): any {
    if (this.markdownRenderer) return this.markdownRenderer

    const renderer = new marked.Renderer()
    renderer.link = (href: string, title: string, text: string) => {
      const safeHref = this.getSafeMarkdownUrl(href)
      if (!safeHref) return text
      const titleAttribute = title ? ` title="${this.escapeAttribute(title)}"` : ''
      return `<a href="${this.escapeAttribute(safeHref)}"${titleAttribute} target="_blank" rel="noopener noreferrer">${text}</a>`
    }
    renderer.image = (href: string, title: string, text: string) => text || ''
    renderer.html = (html: string) => this.escapeHtml(html)
    this.markdownRenderer = renderer
    return renderer
  }

  private getSafeMarkdownUrl (href: string): string {
    const trimmedHref = (href || '').trim()
    if (/^(https?:|mailto:)/i.test(trimmedHref)) return trimmedHref
    if (/^[#/]/.test(trimmedHref)) return trimmedHref
    return ''
  }

  private escapeAttribute (content: string): string {
    return this.escapeHtml(content).replace(/"/g, '&quot;')
  }
}
